import {
  useState, useRef, useEffect 
} from 'react';
import {
  Send,
  Loader2,
  FileText,
  CheckCircle,
  Clock,
  Image,
  X,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useDiscrepancyMessages,
  useSendMessage,
  useAcceptResolution,
  useRejectResolution,
} from '../../hooks/use-discrepancies';
import type {
  Discrepancy, Message 
} from '../../types/discrepancy';
import {
  DISCREPANCY_TYPE_LABELS, RESOLUTION_TYPE_LABELS 
} from '../../types/discrepancy';
import { StatusBadge } from './StatusBadge';
import { MessageBubble } from './MessageBubble';

interface ResolutionRoomProps {
  readonly discrepancy: Discrepancy;
  readonly compact?: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function groupMessagesByDate(msgs: Message[]) {
  const groups: Record<string, Message[]> = {};
  for (const msg of msgs) {
    const date = formatDate(msg.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
  }
  return groups;
}

export function ResolutionRoom({
  discrepancy, compact = false 
}: ResolutionRoomProps) {
  const [message, setMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data, isLoading 
  } = useDiscrepancyMessages(discrepancy.discrepancyId);
  const sendMessageMutation = useSendMessage();
  const acceptResolutionMutation = useAcceptResolution();
  const rejectResolutionMutation = useRejectResolution();

  const messages = data?.data ?? [];
  const isResolved = discrepancy.status === 'resolved';
  const canConfirm = discrepancy.status === 'pending_receiver_confirmation';
  const groupedMessages = groupMessagesByDate(messages);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!message.trim() && selectedImages.length === 0) return;
    sendMessageMutation.mutate({
      discrepancyId: discrepancy.discrepancyId,
      content: message.trim() || `Attached ${selectedImages.length} image(s)`,
      attachments: selectedImages.length > 0 ? selectedImages : undefined,
    });
    setMessage('');
    setSelectedImages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    setSelectedImages((prev) => [...prev, ...imageFiles].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAccept = () => {
    acceptResolutionMutation.mutate(discrepancy.discrepancyId);
  };

  const handleReject = () => {
    const reason = window.prompt('Please provide a reason for rejecting this resolution:');
    if (reason) {
      rejectResolutionMutation.mutate({
        discrepancyId: discrepancy.discrepancyId,
        reason 
      });
    }
  };

  const removeImage = (file: File) => {
    setSelectedImages((prev) => prev.filter((f) => f !== file));
  };

  return (
    <>
      <Card className={`flex flex-col ${compact ? 'h-[400px]' : 'h-[600px]'} border-slate-200`}>
        <ResolutionRoomHeader
          discrepancy={discrepancy}
          isResolved={isResolved}
          canConfirm={canConfirm}
          onAccept={handleAccept}
          onReject={handleReject}
          acceptPending={acceptResolutionMutation.isPending}
          rejectPending={rejectResolutionMutation.isPending}
        />

        <CardContent className="flex-1 overflow-y-auto p-4 bg-white">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          )}
          {!isLoading && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FileText className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No messages yet</p>
              <p className="text-slate-400 text-xs mt-1">Add details or evidence</p>
            </div>
          )}
          {!isLoading && messages.length > 0 && (
            <div className="space-y-1">
              {Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 h-px bg-slate-100" />
                    <span className="text-xs text-slate-400 font-medium">{date}</span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  {msgs.map((msg) => (
                    <MessageBubble key={msg.messageId} message={msg} onImageClick={setPreviewImage} />
                  ))}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </CardContent>

        {selectedImages.length > 0 && (
          <SelectedImagesPreview images={selectedImages} onRemove={removeImage} />
        )}

        {!isResolved && (
          <MessageInput
            message={message}
            setMessage={setMessage}
            inputRef={inputRef}
            fileInputRef={fileInputRef}
            onSend={handleSend}
            onKeyDown={handleKeyDown}
            onImageSelect={handleImageSelect}
            isPending={sendMessageMutation.isPending}
            hasImages={selectedImages.length > 0}
          />
        )}
      </Card>

      {previewImage && <ImagePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />}
    </>
  );
}

interface ResolutionRoomHeaderProps {
  readonly discrepancy: Discrepancy;
  readonly isResolved: boolean;
  readonly canConfirm: boolean;
  readonly onAccept: () => void;
  readonly onReject: () => void;
  readonly acceptPending: boolean;
  readonly rejectPending: boolean;
}

function ResolutionRoomHeader({
  discrepancy,
  isResolved,
  canConfirm,
  onAccept,
  onReject,
  acceptPending,
  rejectPending,
}: ResolutionRoomHeaderProps) {
  return (
    <CardHeader className="pb-3 border-b border-slate-100 flex-shrink-0 bg-slate-50/50">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CardTitle className="text-base font-medium text-slate-900">
              {DISCREPANCY_TYPE_LABELS[discrepancy.type]}
            </CardTitle>
            <StatusBadge status={discrepancy.status} />
          </div>
          <p className="text-sm text-slate-500 truncate">
            {discrepancy.poNumber} • {discrepancy.supplierName}
          </p>
        </div>
      </div>

      <div className="mt-3 p-2 rounded-md bg-white border border-slate-100">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {discrepancy.items.map((item) => (
            <span key={item.lineId} className="text-slate-600">
              <span className="font-medium">{item.itemName}</span>
              <span className="text-slate-400 ml-1">
                (-{item.affectedQuantity} {item.unit})
              </span>
            </span>
          ))}
        </div>
      </div>

      {(discrepancy.proposedResolution ?? discrepancy.finalResolution) && (
        <div
          className={`mt-3 p-3 rounded-md border ${
            isResolved ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isResolved ? (
                <CheckCircle className="h-4 w-4 text-emerald-600" />
              ) : (
                <Clock className="h-4 w-4 text-slate-500" />
              )}
              <span className={`text-sm font-medium ${isResolved ? 'text-emerald-700' : 'text-slate-700'}`}>
                {isResolved ? 'Resolved' : 'Supplier Proposed'}:{' '}
                {RESOLUTION_TYPE_LABELS[discrepancy.finalResolution ?? discrepancy.proposedResolution ?? 'credit_note']}
              </span>
              {discrepancy.creditNoteAmount && (
                <span className={`text-sm ${isResolved ? 'text-emerald-600' : 'text-slate-500'}`}>
                  • {discrepancy.currency} {discrepancy.creditNoteAmount.toFixed(2)}
                </span>
              )}
            </div>
            {canConfirm && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={onReject} disabled={rejectPending} className="text-slate-600">
                  <ThumbsDown className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button size="sm" onClick={onAccept} disabled={acceptPending} className="bg-emerald-600 hover:bg-emerald-700">
                  <ThumbsUp className="h-4 w-4 mr-1" />
                  Accept
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </CardHeader>
  );
}

interface SelectedImagesPreviewProps {
  readonly images: File[];
  readonly onRemove: (file: File) => void;
}

function SelectedImagesPreview({
  images, onRemove 
}: SelectedImagesPreviewProps) {
  return (
    <div className="border-t border-slate-100 p-3 bg-slate-50">
      <div className="flex gap-2 flex-wrap">
        {images.map((file) => (
          <div key={`selected-${file.name}-${file.size}`} className="relative">
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="h-16 w-16 object-cover rounded-md border border-slate-200"
            />
            <button
              type="button"
              onClick={() => onRemove(file)}
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

interface MessageInputProps {
  readonly message: string;
  readonly setMessage: (msg: string) => void;
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
  readonly fileInputRef: React.RefObject<HTMLInputElement | null>;
  readonly onSend: () => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly isPending: boolean;
  readonly hasImages: boolean;
}

function MessageInput({
  message,
  setMessage,
  inputRef,
  fileInputRef,
  onSend,
  onKeyDown,
  onImageSelect,
  isPending,
  hasImages,
}: MessageInputProps) {
  return (
    <div className="border-t border-slate-100 p-3 bg-slate-50/50 flex-shrink-0">
      <div className="flex gap-2">
        <input type="file" ref={fileInputRef} onChange={onImageSelect} accept="image/*" multiple className="hidden" />
        <Button
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          className="border-slate-200 text-slate-500 hover:text-slate-700"
          title="Attach evidence photos"
        >
          <Image className="h-4 w-4" />
        </Button>
        <input
          ref={inputRef}
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type a message..."
          className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 placeholder:text-slate-400"
          disabled={isPending}
        />
        <Button variant="outline" onClick={onSend} disabled={(!message.trim() && !hasImages) || isPending} className="border-slate-200">
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

interface ImagePreviewModalProps {
  readonly imageUrl: string;
  readonly onClose: () => void;
}

function ImagePreviewModal({
  imageUrl, onClose 
}: ImagePreviewModalProps) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={imageUrl}
        alt="Preview"
        className="max-w-full max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
